import { convertSvgTransformToCss } from 'src/helpers';
import { getYoutubeCode } from '../utils/youtube';
import { applyDimensionsFromElement, getDimensionsFromAttributes } from '../utils/dimensions';
import { isSafari } from 'src/utils/safari';

const videoElementSelector = '.element.video:not([data-video-type="youtube"])';
let svgCanvas;

const NS = {
  SVG: 'http://www.w3.org/2000/svg',
  XHTML: 'http://www.w3.org/1999/xhtml',
  XLINK: 'http://www.w3.org/1999/xlink'
};

export default (newCanvas) => {
  svgCanvas = newCanvas;
  initDragListeners();
  initListeners();
};

function initDragListeners () {
  // Moves the video player when the video element is dragged

  let videoElements, transforms, elements, startX, startY, deltaX, deltaY, raf;

  function touchStart (event) {
    mouseDown(event.touches[0]);
  }

  function touchMove (event) {
    mouseMove(event.touches[0]);
  }

  function touchEnd (event) {
    mouseUp(event.touches[0]);
  }

  function mouseDown (event) {
    elements = [];
    videoElements = [...svgCanvas.getSelectedElems()].filter(isVideoElement);

    let target = event.target;
    const getVideoPreviewSelector = video => `#video-preview-${video.id}`;

    // If mouse target is outside canvas
    if (target.closest('#outsideCanvasSelectorGroup')) {
      const relatedElement = document.getElementById(target.dataset.relatedId.substr(1));
      if (relatedElement) target = relatedElement;
    }

    // Check if restricted before allowing movement action
    if (INKIVE.RestrictionsFacade.isRestrictedDesign() && target.closest('.element')) {
      const { id } = target.closest('.element');
      const isRestricted = INKIVE.RestrictionsFacade.getElementRestriction(id, 'video_position') === false;
      if (isRestricted) return;
    }

    if (
      target &&
      target.closest('#svgcontent') &&
      videoElements.length &&
      (target.classList.contains('.element') || target.closest('.element')) &&
      !target.classList.contains('.locked') &&
      !target.closest('.element').closest('.locked')
    ) {
      elements = document.querySelectorAll(videoElements.map(getVideoPreviewSelector).join(','));
      transforms = videoElements.map(video => {
        return convertSvgTransformToCss(video.getAttribute('transform'));
      });
      startX = event.clientX;
      startY = event.clientY;
      document.addEventListener('mousemove', mouseMove, { passive: true });
      document.addEventListener('touchmove', touchMove, { passive: true });
      document.addEventListener('mouseup', mouseUp, { passive: true });
      document.addEventListener('touchend', touchEnd, { passive: true });
    }
  }

  function mouseMove (event) {
    if (!raf) {
      deltaX = event.clientX - startX;
      deltaY = event.clientY - startY;
      raf = requestAnimationFrame(mouseMoveRaf);
    }
  }

  function mouseMoveRaf () {
    elements.forEach((element, i) => {
      const transform = transforms[i];
      element.style.transform = `translate3d(${deltaX}px, ${deltaY}px, 0px) ${transform}`;
    });
    raf = null;
  }

  function mouseUp () {
    document.removeEventListener('mousemove', mouseMove);
    document.removeEventListener('touchmove', touchMove);
    document.removeEventListener('mouseup', mouseUp);
    document.removeEventListener('touchend', touchEnd);
    if (raf) {
      cancelAnimationFrame(raf);
      raf = null;
    }
    elements.forEach((element, i) => {
      const transform = transforms[i];
      element.style.transform = `translate3d(0px,0px,0px) ${transform}`;
    });
    deltaX = deltaY = null;

    videoElements.forEach(updateVideoPlayerSize);
  }

  document.addEventListener('mousedown', mouseDown, { passive: true });
  document.addEventListener('touchstart', touchStart, { passive: true });
}

export function addElement (attr) {
  // Create a wrapping a tag to prevent videos being treated as images
  // (stops upload of thumbnails to user library)
  const videoElement = svgCanvas.addSvgElementFromJson({
    element: 'a',
    attr: {
      id: attr.id || svgCanvas.getNextId(),
      class: 'element video layoutBox quick-delete',
      'xlink:href': attr.source || INKIVE.Helpers.getCORSSecureUrl(attr.url),
      'data-duration': attr.duration || '',
      'data-video-type': 'mp4'
    }
  });

  //  Note: thumbnails are in assets/js/editor/factory/library/video-model.js
  const videoElementThumbnail = svgCanvas.addSvgElementFromJson({
    element: 'image',
    attr: {
      x: attr.position.x,
      y: attr.position.y,
      id: svgCanvas.getNextId(),
      width: attr.size.width,
      height: attr.size.height,
      'xlink:href': attr.thumb_src
    }
  });

  videoElement.appendChild(videoElementThumbnail);

  const createPath = () => {
    // Creates a 0x0px path
    // Needed for repositioning the element
    const bbox = svgedit.utilities.getBBox(videoElement);
    const x = Math.floor4(bbox.x + bbox.width / 2);
    const y = Math.floor4(bbox.y + bbox.height / 2);

    return `M ${x},${y} ${x},${y} z`;
  };

  // Adding a path forces selected element to be the a tag rather than image
  const videoElementPath = svgCanvas.addSvgElementFromJson({
    element: 'path',
    attr: {
      id: svgCanvas.getNextId(),
      d: createPath(),
      fill: '#FFFFFF'
    }
  });
  videoElement.appendChild(videoElementPath);

  // Also updates the thumbnail dimensions
  addVideoPlayer(videoElement, videoElement.getAttribute('xlink:href'));
  toggleVideoPreviewElement(videoElement.id, false);

  return videoElement;
}

function filterVideoElements (elements) {
  if (!Array.isArray(elements)) return [];
  return elements.filter(element => INKIVE.DesignFacade.getType(element) === 'video');
}

function initListeners () {
  INKIVE.EditorFacade.on('select', (selectedElements) => onSelect(selectedElements));

  INKIVE.EditorFacade.on('align.end', (elements) => {
    filterVideoElements(elements).forEach(updateVideoPlayerSize);
  });

  INKIVE.EditorFacade.on('dimensions.changed', ({ element_id: elementId }) => {
    const element = document.getElementById(elementId);
    if (!element?.id || INKIVE.DesignFacade.getType(element) !== 'video') return;

    toggleVideoPreviewElement(elementId, true);
  });

  // Re-initialise player when replacing
  INKIVE.DesignFacade.on('video.update', onUpdate);
  INKIVE.DesignFacade.on('video.replace', onUpdate);

  // Hide player when resizing video
  INKIVE.EditorFacade.on('resize.start', ({ element_id: id }) => {
    toggleVideoPreviewElement(id, false);
  });

  // Hide player when rotating video
  INKIVE.EditorFacade.on('rotate.start', ({ element_id: id }) => {
    toggleVideoPreviewElement(id, false);
  });

  // Show player when done rotating video
  INKIVE.EditorFacade.on('rotate.end', ({ element_id: id }) => {
    toggleVideoPreviewElement(id, true);
  });

  // Hide player when hiding sidebar
  INKIVE.EditorFacade.on('sidebarpanel.toggle', () => {
    document.querySelectorAll(`#svgcontent ${videoElementSelector}.selected`)
      .forEach(elem => toggleVideoPreviewElement(elem.id, false));
  });

  // Show player when showing sidebar
  INKIVE.EditorFacade.on('zoom.changed', () => {
    document.querySelectorAll(`#svgcontent ${videoElementSelector}.selected`)
      .forEach(elem => toggleVideoPreviewElement(elem.id, true));
  });

  // Delete player when deleting video element
  INKIVE.DesignFacade.on('delete', (elemId) => {
    const videoPlayer = document.getElementById(`video-preview-${elemId}`);
    if (videoPlayer) videoPlayer.remove();
  });

  // Delete player when cutting video element
  INKIVE.DesignFacade.on('cut', (elements) => {
    filterVideoElements(elements).forEach(element => {
      const videoPlayer = document.getElementById(`video-preview-${element.id}`);
      if (videoPlayer) videoPlayer.remove();
    });
  });

  // Delete player when changing pages
  INKIVE.EditorFacade.on('page.move_to', () => {
    document.querySelectorAll('.video-preview')
      .forEach(videoPlayer => videoPlayer.remove());
  });

  // Support groups for video player
  INKIVE.DesignFacade.on('group.create', ({ id }) => {
    [...document.querySelectorAll(`#${id} > ${videoElementSelector}`)]
      .forEach(({ id: elemId }) => {
        const videoPlayer = document.getElementById(`video-preview-${elemId}`);
        if (videoPlayer) videoPlayer.classList.add(`.video-preview-${id}`);
      });
  });

  // Remove video player support when deleting a group
  INKIVE.DesignFacade.on('group.ungroup', () => {
    document.querySelectorAll('.video-preview').forEach(videoElement => {
      videoElement.className = [...videoElement.classList].filter(c => {
        const id = c.split('video-preview-')[1];
        return !id || document.getElementById(id);
      }).join(' ');
    });
  });
}

export function initialiseVideoPlayer (videoElement) {
  const videoPlayer = document.getElementById(`video-preview-${videoElement.id}`);
  const g = videoElement.querySelector('g');
  const canCreateNewVideoPlayer = (!videoPlayer || (!isSafari() && !g));

  if (
    videoElement &&
    videoElement.getAttribute('xlink:href') &&
    videoElement.dataset.videoType === 'mp4' &&
    canCreateNewVideoPlayer
  ) {
    if (g) g.remove();
    deleteVideoPlayer(videoElement);
    addVideoPlayer(videoElement, videoElement.getAttribute('xlink:href'));
  }
}

function onSelect (selectedElements) {
  const selectedVideoElements = selectedElements
    .map(selectedElement => {
      if (!selectedElement) return selectedElement;
      return selectedElement.id === 'background'
        ? document.querySelector('#svgcontent > g.background')
        : document.getElementById(selectedElement.id);
    })
    .filter(isVideoElement);

  // Initialise video player elements after save
  selectedVideoElements.forEach(initialiseVideoPlayer);

  // Pause video player & hide controls when video deselected
  const videoElements = document.querySelectorAll(videoElementSelector);
  for (const videoElement of videoElements) {
    const show = selectedVideoElements.includes(videoElement);
    toggleVideoPreviewElement(videoElement.id, show);
  }

  INKIVE.Tool.getInstance().editor.canvas.showOverlayElements();
}

function onUpdate (elementData) {
  if (!elementData) return;

  const element = document.getElementById(elementData.id);

  if (element) {
    const isYoutube = getYoutubeCode(elementData.source);
    const g = element.querySelector('g');

    if (!('locked' in elementData)) {
      // Remove player when replacing with yt or mp4
      if (g) g.remove();
      deleteVideoPlayer(element);
    }

    if (elementData.source && !isYoutube) {
      // add player when replacing with mp4
      addVideoPlayer(element, elementData.source);
    }
  }
}

function replaceOldVideoElemWithNew (element) {
  const thumbnail = element.querySelector('image');
  const dimensions = getDimensionsFromAttributes(thumbnail);
  const attributes = {
    id: element.id,
    source: element.getAttribute('xlink:href') || element.dataset.src,
    duration: element.dataset.duration,
    thumb_src: element.getAttribute('xlink:href'),
    position: {
      x: dimensions.x,
      y: dimensions.y
    },
    size: {
      width: dimensions.width,
      height: dimensions.height
    }
  };

  element.remove();

  return INKIVE.DesignFacade.create('video', attributes);
}

function isVideoElement (element) {
  if (!element) return false;
  return [element, element.parentElement, element.closest('.video.element')]
    .find((videoElement) => {
      if (!videoElement) return false;
      const { classList, dataset } = videoElement;
      const notYoutubeElement = dataset.videoType !== 'youtube';
      const hasVideoElementClasses = classList.contains('element') && classList.contains('video');
      const groupHasVideoElement = classList.contains('group') && element.querySelector(videoElementSelector);
      return notYoutubeElement && (hasVideoElementClasses || groupHasVideoElement);
    });
}

export function deleteVideoPlayer (element) {
  const videoPlayer = document.getElementById(`video-preview-${element.id}`);
  if (videoPlayer) videoPlayer.remove();
}

function addVideoPlayer (videoElement, videoSrc) {
  // Create video element
  const video = document.createElementNS(NS.XHTML, 'video');
  video.id = `video-${videoElement.id}`;
  video.crossOrigin = '*'; // 'anonymous' - Enables same-origin CORS policy
  video.preload = 'metadata'; // Don't preload whole video
  video.playsinline = true;
  video.muted = false;
  video.controls = false;
  const source = document.createElement('source');
  source.src = videoSrc;
  video.append(source);

  if (!isSafari()) {
  // Add video element to foreignObject
    const foreignObject = document.createElementNS(NS.SVG, 'foreignObject');
    foreignObject.append(video);

    // Add foreignObject to g
    const g = document.createElementNS(NS.SVG, 'g');
    g.append(foreignObject);

    // Add g to videoElement
    g.dataset.sanitize = 'ignore';
    videoElement.append(g);
  }

  // Create video player
  const videoPlayer = document.createElement('div');
  videoPlayer.classList.add('video-preview');
  videoPlayer.id = `video-preview-${videoElement.id}`;

  // Removing video player when video element is replaced
  deleteVideoPlayer(videoPlayer);

  // Create controls
  const videoControls = createVideoControls(video);
  videoPlayer.append(videoControls);

  // Insert video player before selector grips
  const attachPoint = document.querySelector('#canvas-panel') || document.body;
  attachPoint.append(videoPlayer);

  updateVideoPlayerSize(videoElement);

  // Set initial values once the video is loaded
  video.addEventListener('loadedmetadata', () => {
    videoElement.dataset.duration = video.duration * 1000;

    const progress = videoControls.querySelector('.video-controls-progress');
    const totalTime = videoControls.querySelector('.total-time');
    progress.setAttribute('max', video.duration);
    totalTime.innerHTML = toMss(video.duration);

    updateVideoPlayerSize(videoElement);

    INKIVE.DesignFacade.trigger('video.loadedmetadata', { id: videoElement.id });
  });
}

function toggleVideoPreviewElement (videoId, show) {
  const element = document.getElementById(videoId);
  const videoPlayer = document.getElementById(`video-preview-${videoId}`);

  if (!isVideoElement(element) || !videoPlayer) return;

  const video = isSafari() ? videoPlayer.querySelector('video') : element.querySelector('video');

  if (!video) return;

  if (show) {
    videoPlayer.classList.remove('visibility-hidden');
    updateVideoPlayerSize(element);
  } else {
    video.pause();
    videoPlayer.classList.add('visibility-hidden');
  }
}

function createVideoControls (video) {
  const div = document.createElement('div');
  div.innerHTML = `
    <div class="video-controls">
      <button class="video-controls-playpause" data-state="play">
        <span class="video-play">
          <svg fill="#fff">
            <use xlink:href="#video-play"></use>
          </svg>
        </span>
        <span class="video-pause">
          <svg fill="#fff">
            <use xlink:href="#video-pause"></use>
          </svg>
        </span>
      </button>
      <div class="progress">
        <div class="progress-inner">
          <span class="current-time">00:00</span>
          <progress class="video-controls-progress" value="0" min="0">
            <span class="video-controls-progress-bar"></span>
          </progress>
          <span class="total-time">00:00</span>
        </div>
      </div>
    </div>
  `.trim();

  const videoControls = div.firstChild;
  isSafari() && videoControls.insertBefore(video, videoControls.firstChild);
  const progressWrapper = videoControls.querySelector('.progress-inner');
  const currentTime = videoControls.querySelector('.current-time');
  const playpause = videoControls.querySelector('.video-controls-playpause');
  const progress = videoControls.querySelector('.video-controls-progress');
  const progressBar = videoControls.querySelector('.video-controls-progress-bar');
  let grab = false;

  const changeButtonState = () => {
    if (video.paused || video.ended) {
      playpause.setAttribute('data-state', 'play');
    } else {
      playpause.setAttribute('data-state', 'pause');
    }
  };

  // TODO: remove event listeners when element is deleted or page is changed
  video.addEventListener('timeupdate', () => {
    if (!grab) progress.value = video.currentTime;
    progressBar.style.width = Math.floor((video.currentTime / video.duration) * 100) + '%';
    currentTime.innerHTML = toMss(video.currentTime);
  });

  video.addEventListener('play', changeButtonState, false);

  video.addEventListener('pause', changeButtonState, false);

  playpause.addEventListener('click', () => {
    if (video.paused || video.ended) video.play();
    else video.pause();
  });

  progressWrapper.addEventListener('mousedown', (e) => {
    grab = true;
    updatePos(e);
    e.stopPropagation();
    return false;
  });

  document.addEventListener('mousemove', (e) => {
    if (grab) updatePos(e);
  });

  document.addEventListener('mouseup', (e) => {
    if (grab) updatePos(e);
    grab = false;
  });

  function updatePos (e) {
    const x = e.pageX - progress.getBoundingClientRect().x;
    const percentage = (x / progress.offsetWidth);
    const currentTimeInSec = percentage * progress.max;
    progress.value = currentTimeInSec;
    if (currentTimeInSec < 0) {
      currentTime.innerHTML = '00:00';
    } else if (currentTimeInSec > video.duration) {
      currentTime.innerHTML = toMss(video.duration);
    } else {
      currentTime.innerHTML = toMss(currentTimeInSec);
    }
    video.currentTime = currentTimeInSec;
  }

  return videoControls;
}

export function updateVideoPlayerSize (element) {
  const videoElement = element.classList.contains('element')
    ? element
    : element.closest('.element');
  const image = videoElement.querySelector('image');
  const layoutBox = videoElement.hasAttribute('clip-path') && svgedit.utilities.getClipPath(videoElement)
    ? svgedit.utilities.getClipPath(videoElement).childNodes[0]
    : videoElement.querySelector('image');

  applyDimensionsFromElement({ from: element, to: image });

  const videoPlayer = document.getElementById(`video-preview-${videoElement.id}`);
  if (!videoPlayer) return;
  const playpause = videoPlayer.querySelector('.video-controls-playpause');
  const progress = videoPlayer.querySelector('.progress');

  // Element dimensions should be calculated without transforms applied
  const transform = videoElement.getAttribute('transform');
  if (transform) videoElement.removeAttribute('transform');
  const { width, height, x, y } = layoutBox.getBoundingClientRect();
  if (transform) videoElement.setAttribute('transform', transform);

  videoPlayer.style.opacity = videoElement.getAttribute('opacity');
  videoPlayer.style.transform = convertSvgTransformToCss(transform);
  videoPlayer.style.width = `${width}px`;
  videoPlayer.style.height = `${height}px`;
  videoPlayer.style.top = `${y}px`;
  videoPlayer.style.left = `${x}px`;

  if (isSafari()) {
    const video = videoPlayer.querySelector('video');

    if (!video) return;

    const imageTransform = image.getAttribute('transform');

    if (imageTransform) image.removeAttribute('transform');

    const { width: imageWidth, height: imageHeight, x: imageX, y: imageY } = image.getBoundingClientRect();

    if (imageTransform) image.setAttribute('transform', imageTransform);

    video.style.position = 'absolute';
    video.style.width = `${imageWidth}px`;
    video.style.height = `${imageHeight}px`;
    video.style.top = `${imageY - y}px`;
    video.style.left = `${imageX - x}px`;
  } else {
    const foreignObject = videoElement.querySelector('foreignObject');

    if (foreignObject) {
      const video = foreignObject.querySelector('video');
      video.width = image.getAttribute('width');
      video.height = image.getAttribute('height');

      applyDimensionsFromElement({ from: image, to: foreignObject });
    }
  }

  if (progress) {
    const minSize = 100;
    if (height < minSize || width < minSize) {
      progress.classList.add('min-size');
      playpause.classList.add('min-size');
    } else {
      progress.classList.remove('min-size');
      playpause.classList.remove('min-size');
    }
  }
}

function toMss (s) {
  const date = new Date(0);
  date.setSeconds(Math.round(s));
  return date.toISOString().substr(14, 5);
}

export function backwardsCompatibility (element) {
  const isYoutubeVideo = !!getYoutubeCode(element.getAttribute('xlink:href'));
  // Backwards compatibility for <image class="video"> syntax
  if (element.nodeName === 'image' || !element.hasAttribute('clip-path')) {
    element = replaceOldVideoElemWithNew(element);
  }
  if (typeof element === 'string') return;
  // Backwards compatibility for video-elem class
  if (element.classList.contains('video-elem')) {
    element.classList.remove('video-elem');
    element.classList.add('video');
  }

  if (!element.dataset.videoType) {
    element.dataset.videoType = isYoutubeVideo
      ? 'youtube'
      : 'mp4';
  }
}
